import React, { useEffect, useState, useContext } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import brLocale from "date-fns/locale/pt-BR";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Box,
  Chip,
} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: 440,
    "& .MuiTableCell-head": {
      fontWeight: 700,
      backgroundColor: theme.palette.background.default,
      position: "sticky",
      top: 0,
      zIndex: 1,
    },
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(0.5),
    fontWeight: 600,
  },
  totalRow: {
    "& td": {
      fontWeight: 700,
      borderTop: `2px solid ${theme.palette.divider}`,
    },
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
}));

const METRIC_LABELS = {
  ticketsAbertos: "Tickets Abertos",
  ticketsInteragidos: "Tickets Interagidos",
  ticketsReceptivos: "Receptivos",
  ticketsAtivos: "Ativos",
  transferenciasRealizadas: "Transferências",
  ticketsClassificados: "Classificados",
};

const METRIC_COLORS = [
  "#2196F3", // Abertos - azul
  "#4CAF50", // Interagidos - verde
  "#FF9800", // Receptivos - laranja
  "#9C27B0", // Ativos - roxo
  "#F44336", // Transferencias - vermelho
  "#00BCD4", // Classificados - ciano
];

const METRIC_KEYS = Object.keys(METRIC_LABELS);

export const UserMetricsDetail = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [initialDate, setInitialDate] = useState(new Date());
  const [finalDate, setFinalDate] = useState(new Date());
  const [metricsData, setMetricsData] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState(() => new Set(METRIC_KEYS));
  const { user } = useContext(AuthContext);

  const toggleFilter = (key) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    if (user?.companyId) {
      handleFetchMetrics();
    }
  }, [user?.companyId]);

  const handleFetchMetrics = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/dashboard/user-metrics", {
        params: {
          initialDate: format(initialDate, "yyyy-MM-dd"),
          finalDate: format(finalDate, "yyyy-MM-dd"),
        },
      });
      setMetricsData(data.data || []);
    } catch (error) {
      toast.error("Erro ao buscar métricas detalhadas");
    } finally {
      setLoading(false);
    }
  };

  // --- Chart data: filtered by active badges ---
  const visibleKeys = METRIC_KEYS.filter((key) => activeFilters.has(key));

  const chartData = {
    labels: metricsData.map((item) => item.nome),
    datasets: visibleKeys.map((key) => {
      const idx = METRIC_KEYS.indexOf(key);
      return {
        label: METRIC_LABELS[key],
        data: metricsData.map((item) => item[key] || 0),
        backgroundColor: METRIC_COLORS[idx],
        borderRadius: 4,
        barPercentage: 0.7,
      };
    }),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        display: true,
        labels: {
          usePointStyle: true,
          pointStyle: "rectRounded",
          font: { size: 11 },
        },
      },
      title: {
        display: false,
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        stacked: false,
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: { size: 10 },
        },
      },
      y: {
        stacked: false,
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0,
        },
      },
    },
  };

  // --- Totals ---
  const totals = METRIC_KEYS.reduce((acc, key) => {
    acc[key] = metricsData.reduce((sum, item) => sum + (item[key] || 0), 0);
    return acc;
  }, {});

  return (
    <>
      <Typography component="h2" variant="h6" color="primary" gutterBottom>
        Métricas Detalhadas por Atendente
      </Typography>

      <div className={classes.filterRow}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={brLocale}
        >
          <DatePicker
            value={initialDate}
            onChange={(newValue) => setInitialDate(newValue)}
            label="Data Inicial"
            renderInput={(params) => (
              <TextField fullWidth {...params} sx={{ width: "20ch" }} />
            )}
          />
        </LocalizationProvider>

        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={brLocale}
        >
          <DatePicker
            value={finalDate}
            onChange={(newValue) => setFinalDate(newValue)}
            label="Data Final"
            renderInput={(params) => (
              <TextField fullWidth {...params} sx={{ width: "20ch" }} />
            )}
          />
        </LocalizationProvider>

        <Button
          style={{
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
          }}
          onClick={handleFetchMetrics}
          variant="contained"
          disabled={loading}
        >
          {loading ? "Carregando..." : "Filtrar"}
        </Button>
      </div>

      {/* Chips de resumo - clicáveis como filtro */}
      <div style={{ marginBottom: 8 }}>
        {METRIC_KEYS.map((key, idx) => {
          const isActive = activeFilters.has(key);
          return (
            <Chip
              key={key}
              label={`${METRIC_LABELS[key]}: ${totals[key] || 0}`}
              className={classes.chip}
              clickable
              onClick={() => toggleFilter(key)}
              variant={isActive ? "default" : "outlined"}
              style={{
                backgroundColor: isActive ? METRIC_COLORS[idx] : "transparent",
                color: isActive ? "#fff" : METRIC_COLORS[idx],
                border: isActive ? "none" : `2px solid ${METRIC_COLORS[idx]}`,
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              size="small"
            />
          );
        })}
      </div>

      {/* Tabs: gráfico vs tabela */}
      <Box>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="standard"
        >
          <Tab label="Gráfico" />
          <Tab label="Tabela" />
        </Tabs>
      </Box>

      {/* Tab 0: Gráfico */}
      {tabValue === 0 && (
        <div
          className={classes.tabPanel}
          style={{ height: Math.max(280, metricsData.length * 30 + 100) }}
        >
          {metricsData.length > 0 ? (
            <Bar options={chartOptions} data={chartData} />
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ padding: 20 }}
            >
              Nenhum dado encontrado para o período selecionado.
            </Typography>
          )}
        </div>
      )}

      {/* Tab 1: Tabela */}
      {tabValue === 1 && (
        <TableContainer
          component={Paper}
          className={classes.tableContainer}
          variant="outlined"
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Atendente</TableCell>
                {METRIC_KEYS.map((key, idx) => (
                  <TableCell key={key} align="center">
                    <span style={{ color: METRIC_COLORS[idx], fontWeight: 600 }}>
                      {METRIC_LABELS[key]}
                    </span>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {metricsData.map((row) => (
                <TableRow key={row.userId} hover>
                  <TableCell>{row.nome}</TableCell>
                  {METRIC_KEYS.map((key) => (
                    <TableCell key={key} align="center">
                      {row[key] || 0}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {metricsData.length > 0 && (
                <TableRow className={classes.totalRow}>
                  <TableCell>Total</TableCell>
                  {METRIC_KEYS.map((key) => (
                    <TableCell key={key} align="center">
                      {totals[key] || 0}
                    </TableCell>
                  ))}
                </TableRow>
              )}
              {metricsData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhum dado encontrado para o período selecionado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
